import {useCallback, useState, useRef} from "react";
import {useDispatch} from "react-redux";
import TreeNode from "./TreeNode";
import styles from "./index.module.scss";
import node_styles from "./TreeNode/index.module.scss";

export default function DraggableTree({tab, scene, selectedKeys, selectObject, reorderObject, concealObject, revealObject}) {
  const dispatch = useDispatch();

  const selectNodeObject = useCallback(
    uuid => {
      const target = tab === "world" ? "3D" : "2D";
      dispatch(selectObject(uuid.substring(0, 3), uuid, target));
    },
    [tab],
  );

  const toggleVisibility = useCallback(
    (uuid, isConcealed) => {
      const target = tab === "world" ? "3D" : "2D";
      if (isConcealed) dispatch(revealObject({uuid, target}));
      else dispatch(concealObject({uuid, target}));
    },
    [tab],
  );

  const [isDragging, setIsDragging] = useState(false);
  const draggedNodeUuid = useRef(null);
  const dragoveredNode = useRef(null);

  const onDragStart = useCallback(event => {
    event.dataTransfer.setDragImage(event.target.children[1], 0, 0);
    setIsDragging(true);
    draggedNodeUuid.current = event.target.parentElement.getAttribute("uuid");
    selectNodeObject(draggedNodeUuid.current);
  }, []);

  const onDragEnter = useCallback(
    event => {
      if (dragoveredNode.current) removeHighlight(dragoveredNode.current);
      if (checkValidation(event.currentTarget, draggedNodeUuid.current, scene.uuid) === false) return;
      dragoveredNode.current = event.currentTarget;
      addHighlight(dragoveredNode.current);
    },
    [scene],
  );

  const onDragEnd = useCallback(event => {
    setIsDragging(false);
    draggedNodeUuid.current = null;
    if (dragoveredNode.current) removeHighlight(dragoveredNode.current);
    dragoveredNode.current = null;
  }, []);

  const onDragOver = useCallback(event => {
    event.preventDefault();
  }, []);

  const onDrop = useCallback(
    event => {
      event.preventDefault();
      if (checkValidation(event.currentTarget, draggedNodeUuid.current, scene.uuid) === false) return;
      const location = event.currentTarget.getAttribute("location");
      const dropTargetNodeUuid =
        location === "node"
          ? event.currentTarget.parentElement.getAttribute("uuid")
          : event.currentTarget.parentElement.parentElement.getAttribute("uuid");
      const dropInfo = {draggedNodeUuid: draggedNodeUuid.current, dropTargetNodeUuid, droppedLocation: location};
      const target = tab === "world" ? "3D" : "2D";
      dispatch(reorderObject({info: dropInfo, target}));
      onDragEnd();
    },
    [scene],
  );

  return (
    <div className={styles.container} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <TreeNode
        key={scene.uuid}
        nodeObject={scene}
        depth={0}
        isRootNode={true}
        selectedKeys={selectedKeys}
        selectNodeObject={selectNodeObject}
        toggleVisibility={toggleVisibility}
        isDragging={isDragging}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDrop={onDrop}
      />
    </div>
  );
}

function addHighlight(element) {
  const location = element.getAttribute("location");
  switch (location) {
    case "node":
      element.classList.add(node_styles.highlighted_node);
      break;
    case "siblingGap":
      element.classList.add(node_styles.highlighted_gap);
      element.parentElement.parentElement.parentElement.previousElementSibling.previousElementSibling.classList.add(
        node_styles.highlighted_node,
      );
      break;
    case "childrenGap":
      element.classList.add(node_styles.highlighted_gap);
      element.parentElement.previousElementSibling.classList.add(node_styles.highlighted_node);
      break;
    default:
      break;
  }
}

function removeHighlight(element) {
  const location = element.getAttribute("location");
  switch (location) {
    case "node":
      element.classList.remove(node_styles.highlighted_node);
      break;
    case "siblingGap":
      element.classList.remove(node_styles.highlighted_gap);
      element.parentElement.parentElement.parentElement.previousElementSibling.previousElementSibling.classList.remove(
        node_styles.highlighted_node,
      );
      break;
    case "childrenGap":
      element.classList.remove(node_styles.highlighted_gap);
      element.parentElement.previousElementSibling.classList.remove(node_styles.highlighted_node);
      break;
    default:
      break;
  }
}

function checkValidation(targetElement, draggedNodeUuid, rootNodeUuid) {
  const uuid = targetElement.getAttribute("uuid");
  if (uuid === draggedNodeUuid) return false;
  else if (uuid === rootNodeUuid) return true;
  return checkValidation(targetElement.parentElement, draggedNodeUuid, rootNodeUuid);
}
